//Event manager provides a hook that performs different mutations on 
//event data and handles multiple methods for mutating the data.
import useAuthenticatedMutation from '@datahooks/admin/useAuthenticatedMutation';
import PropTypes from 'prop-types';
import axios from 'axios';
import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '@home/config';
import logError from '@datahooks/metadata/ErrorLogging';

// Function to perform HTTP requests
const manageEvent = async ({data, method}, token) => {
    let url = `${API_BASE_URL}event_details`;

    if (method === 'DELETE' && data.event_id) {
        url += `?event_id=${data.event_id}`; // Add to URL for DELETE
    }

    try {
        const response = await axios({
            method: method,
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`,
                'x-functions-key': FUNCTION_KEY,
            },
            data: method !== 'DELETE' ? data : {}, // Only send data for non-DELETE requests
        });

        return response.data;
    } catch (error) {
        logError(`Failed to ${method.toLowerCase()} event:`, error, {});
        throw (error?.response?.data) ? error.response.data : error;
    }
};

// Hook to manage event updates
export default function useEventManager(onSuccess, onError) {
    
    const projectScope = [PROJECT_SCOPE];
    const { mutate, isLoading, isError, error, data, reset: resetMutation } = useAuthenticatedMutation(manageEvent, projectScope, {
        onSuccess,
        onError
    });

    // Function to call the mutate function with additional parameters for different HTTP methods
    const performAction = (data, method = 'PUT') => {
        return new Promise((resolve, reject) => {
            mutate(
                { data, method },
                {
                    onSuccess: (result) => {
                        resolve(result);
                        if (onSuccess) onSuccess(result);
                    },
                    onError: (error) => {
                        const err = error instanceof Error ? error : new Error(String(error));
                        reject(err);
                        if (onError) onError(error);
                    },
                }
            );
        });
    };

    return {
        performAction,
        isLoading,
        isError,
        error,
        data,
        reset: resetMutation
    };
}

useEventManager.propTypes = {
    onSuccess: PropTypes.func,
    onError: PropTypes.func
}